import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { LoadingPanel } from "../../LoadingPanel";
import { addNotificationWithTimeout } from '../../../app/addNotificationWithTimeout';
import { handleResponse } from '../../../app/handleResponse';
import { getCSRFToken } from '../../../app/getCSRFToken';
import { Button } from "@progress/kendo-react-buttons";
import { AutoComplete } from "@progress/kendo-react-dropdowns";
import { Label } from "@progress/kendo-react-labels";
import { CommandCell } from '../../forms/CommandCell';
import {
    Grid,
    GridColumn as Column,
} from "@progress/kendo-react-grid";

import './VenueEvent.scss';

export const AdministratorVenueEventPerformers = (props) => {
    const dispatch = useDispatch();

    const editField = "inEdit";

    const [venueEventPerformers, setVenueEventPerformers] = useState([]);
    const [areVenueEventPerformersLoading, setAreVenueEventPerformersLoading] = useState(true);
    const [loadingError, setLoadingError] = useState("");
    const [performers, setPerformers] = useState([]);
    const [performerName, setPerformerName] = useState(null);
    const [arePerformersLoading, setArePerformersLoading] = useState(false);

    const autocompleteTimeout = useRef();
    const autocompleteTimeoutDelay = 500; //Milliseconds
    
    const getVenueEventPerformers = () => {
        setAreVenueEventPerformersLoading(true);

        const dataItem = {
            venueEventKey: props.venueEventKey,
        };
    
        fetch(`/api/administrator/venueeventperformers`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                setAreVenueEventPerformersLoading(false);
                setVenueEventPerformers(result);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setAreVenueEventPerformersLoading(false);
                setLoadingError(error.message);
            });
    }

    const deleteVenueEventPerformer = (venueEventPerformerKey) => {
        const dataItem = {
            venueEventPerformerKey: venueEventPerformerKey
        }

        fetch("/api/administrator/venueeventperformer",
            {
                method: "DELETE",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Deleted item successfully");

                getVenueEventPerformers();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const savePerformer = () => {
        const performer = performers.find(element => element.name === performerName);

        if (!performer) {
            addNotificationWithTimeout(dispatch, "error", "You must select a performer");
            return;
        }

        const dataItem = {
            venueEventKey: props.venueEventKey,
            performerKey: performer.performerKey,
        }

        fetch("/api/administrator/venueeventperformer",
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Created item successfully");

                getVenueEventPerformers();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const discard = (dataItem) => {
        const newVenueEventPerformers = [...venueEventPerformers];
        newVenueEventPerformers.splice(0, 1);
        setVenueEventPerformers(newVenueEventPerformers);
    };

    const remove = (dataItem) => {
        deleteVenueEventPerformer(dataItem.venueEventPerformerKey);
    };

    const MyCommandCell = (props) => (
        <CommandCell
            {...props}
            canEdit={false}
            remove={remove}
            discard={discard}
            editField={editField}
            keyField="venueEventPerformerKey"
        />
    );

    const itemChange = (event) => {
        const field = event.field || "";
        const newVenueEventPerformers = venueEventPerformers.map((item) =>
            item.venueEventPerformerKey === event.dataItem.venueEventPerformerKey
                ? { ...item, [field]: event.value }
                : item
        );
        setVenueEventPerformers(newVenueEventPerformers);
    };

    const performerChanged = (event) => {
        const value = event.value;

        clearTimeout(autocompleteTimeout.current);

        if (!value || value.length < 2) {
            setPerformers([]);
            setArePerformersLoading(false);
            setPerformerName(null);
            return;
        }
        
        autocompleteTimeout.current = setTimeout(() => {
            const dataItem = {
                name: value,
                onlyActive: true,
            };

            fetch(`/api/administrator/performers`,
                {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json',
                        "X-XSRF-TOKEN": getCSRFToken(),
                    },
                    body: JSON.stringify(dataItem),
                })
                .then(handleResponse)
                .then(result => {
                    setPerformers(result.performers);  
                    setPerformerName(value);
                })
                .catch(error => {
                    addNotificationWithTimeout(dispatch, "error", error.message);
                })
                .finally(() => {
                    setArePerformersLoading(false);
                });
        }, autocompleteTimeoutDelay);

        setArePerformersLoading(true);
    };

    useEffect(() => {
        if (props.venueEventKey) {
            getVenueEventPerformers();
        }
    }, []);

    return (
        <div>
            <div className="administratorVenueEventPerformerAdd">
                <Label>Add performer:</Label> 

                <AutoComplete
                    id={"performer"}
                    name={"performer"}
                    data={performers}        
                    placeholder="e.g. Taylor Swift"
                    textField="name"
                    onChange={performerChanged}
                    loading={arePerformersLoading}
                />

                <Button
                    themeColor={"primary"}
                    type={"button"}
                    onClick={savePerformer}
                    >
                    Save
                </Button>
            </div>
            {areVenueEventPerformersLoading ? <LoadingPanel /> : (loadingError === "" ? (
                <Grid
                    data={venueEventPerformers}
                    onItemChange={itemChange}
                    editField={editField}
                    dataItemKey={"venueEventPerformerKey"}
                    resizable={true}
                    scrollable="none"
                >
                    <Column field="performerName" title={"Performer"} />
                    <Column cell={MyCommandCell} width="150px" />
                </Grid>
            ) : <></>)
            }
        </div>
    );
}